import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { orders as ordersApi } from "@/api/orders";
import { tradingAccounts } from "@/api/tradingAccounts";
import { useAuth } from "@/auth/AuthContext";
import { useBranding } from "@/branding/BrandingLoader";
import { Button } from "@/components/ui";

const DISMISSED_KEY = "piggy.onboarding.dismissed";

interface Step {
  id: string;
  title: string;
  body: React.ReactNode;
  done: boolean;
  cta: { label: string; to: string };
}

/**
 * First-run onboarding overlay. Shown when the authenticated user
 * hasn't completed all the recommended setup steps and hasn't
 * explicitly dismissed it. Each step links to the page that resolves
 * it; dismissed once stays dismissed.
 */
export function Onboarding() {
  const { isAuthenticated, user } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  });

  const tradingAccts = useQuery({
    queryKey: ["trading-accounts", "mine"],
    queryFn: () => tradingAccounts.list(true),
    enabled: isAuthenticated,
  });
  const orderHistory = useQuery({
    queryKey: ["orders"],
    queryFn: () => ordersApi.list(),
    enabled: isAuthenticated,
  });

  const steps: Step[] = useMemo(() => {
    const hasBranding = branding ? Object.keys(branding.branding ?? {}).length > 0 : false;
    return [
      {
        id: "verify",
        title: "Verify your email",
        body: "Click the link we sent to your inbox, or resend from your profile.",
        done: !!user?.is_verified,
        cta: { label: "Open profile", to: "/profile" },
      },
      {
        id: "mfa",
        title: "Enable two-factor",
        body: "Add a TOTP authenticator so a stolen password isn't enough to access trading.",
        done: !!user?.totp_enabled,
        cta: { label: "Set up MFA", to: "/profile" },
      },
      {
        id: "trading-account",
        title: "Create a trading account",
        body: "Trading accounts hold your balance and route through the broker pivot. You need at least one to trade.",
        done: (tradingAccts.data?.length ?? 0) > 0,
        cta: { label: "Open accounts", to: "/trading-accounts" },
      },
      {
        id: "branding",
        title: "Brand your tenant",
        body: "Set your primary color and logo so everyone in the org sees your brand.",
        done: hasBranding,
        cta: { label: "Edit branding", to: "/admin/branding" },
      },
      {
        id: "trade",
        title: "Place your first order",
        body: "Pick the active account in the navbar, open Terminal, hit B, set qty, press Enter.",
        done: (orderHistory.data?.orders.length ?? 0) > 0,
        cta: { label: "Open Terminal", to: "/terminal" },
      },
    ];
  }, [user, branding, tradingAccts.data, orderHistory.data]);

  const todo = steps.filter((s) => !s.done);
  const allDone = todo.length === 0;

  useEffect(() => {
    // Auto-dismiss once everything is done so we don't nag.
    if (allDone) {
      localStorage.setItem(DISMISSED_KEY, "1");
      setDismissed(true);
    }
  }, [allDone]);

  if (!isAuthenticated || dismissed || allDone) return null;

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  }

  return (
    <div className="fixed bottom-4 right-4 z-30 w-80 max-w-[calc(100vw-2rem)] term-card shadow-2xl">
      <div className="flex items-center justify-between border-b border-term-border bg-term-elev px-3 py-2">
        <div>
          <div className="text-2xs uppercase tracking-wider text-term-mute">Get started</div>
          <div className="text-xs font-semibold text-term-fg">
            {steps.length - todo.length}/{steps.length} steps complete
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss onboarding"
          className="rounded-sm p-1 text-term-mute hover:bg-term-bg hover:text-term-fg"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
      </div>
      <ul className="max-h-72 overflow-y-auto">
        {steps.map((s) => (
          <li
            key={s.id}
            className={`border-b border-term-border px-3 py-2 ${s.done ? "opacity-60" : ""}`}
          >
            <div className="flex items-start gap-2">
              <span
                className={`mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border ${
                  s.done ? "border-term-up bg-term-up" : "border-term-border"
                }`}
              >
                {s.done && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="3">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                )}
              </span>
              <div className="flex-1">
                <div className="text-xs font-medium text-term-fg">{s.title}</div>
                <p className="mt-0.5 text-2xs text-term-dim">{s.body}</p>
                {!s.done && (
                  <Button
                    variant="ghost"
                    className="mt-1 px-1 py-0.5 text-2xs"
                    onClick={() => navigate(s.cta.to)}
                  >
                    {s.cta.label} →
                  </Button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
